import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { faUsers } from '@fortawesome/pro-light-svg-icons/faUsers';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLRouteNames } from '../../../../routing/sml/SMLRoutes';
import { OutreachShell } from '../components/OutreachShell';
import { useReleaseOptions } from '../hooks/useReleaseOptions';
import { useCommunities } from '../hooks/useCommunities';
import { FindSongPicker } from '../find/FindSongPicker';
import { CommunityTable } from './CommunityTable';
import { communityMessages as messages } from './communityMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CommunitiesScreenProps {
  accountId: number;
  initialSong: string | null;
}

/**
 * "Where you can post yourself" (design outreach r1): the read-only list of communities (Reddit
 * subreddits) that fit the artist's sound, with the posting rules, the self-promo chip and a link
 * out. No pick, no pitch, no campaign: a community has no contact point.
 */
const CommunitiesScreenComponent: React.FC<CommunitiesScreenProps> = ({ accountId, initialSong }) => {
  const intl = useIntl();
  const [song, setSong] = useState<string | null>(initialSong);
  const { options } = useReleaseOptions(accountId);
  const communities = useCommunities(accountId, song);

  const content = useMemo(() => {
    if (communities.isLoading) {
      return <LoadingView />;
    }
    if (communities.error !== null) {
      return <ErrorView error={communities.error ?? undefined} retry={communities.retry} />;
    }
    if (communities.rows.length === 0) {
      return (
        <div className={shared.empty} data-testid="community-empty">
          <span className={shared.emptyIcon}>
            <FontAwesomeIcon icon={faUsers} />
          </span>
          <h3 className={shared.h2}>{intl.formatMessage(messages.empty)}</h3>
          <p className={shared.sub}>{intl.formatMessage(messages.emptyBody)}</p>
        </div>
      );
    }
    return (
      <>
        <CommunityTable rows={communities.rows} />
        <p className={shared.small}>
          <span className={shared.dot} /> {intl.formatMessage(messages.footer, { count: communities.rows.length })}
        </p>
      </>
    );
  }, [communities.error, communities.isLoading, communities.retry, communities.rows, intl]);

  return (
    <OutreachShell
      section="tabs"
      tab={SMLRouteNames.outreachCommunities}
      pitchSong={song}
      heading={intl.formatMessage(messages.title)}
      lead={intl.formatMessage(messages.intro)}
      actions={<FindSongPicker options={options} value={song} onChange={setSong} />}
    >
      <div className={shared.alert} data-testid="community-banner">
        <FontAwesomeIcon icon={faCircleInfo} className={shared.alertIcon} />
        <div>
          <b>{intl.formatMessage(messages.bannerTitle)}</b>
          <p className={shared.small}>{intl.formatMessage(messages.bannerBody)}</p>
        </div>
      </div>
      {content}
    </OutreachShell>
  );
};

export const CommunitiesScreen = TypedMemo(CommunitiesScreenComponent);
